import React, { memo, useEffect, useMemo } from 'react'

import {
  Notice,
  Icon,
  RotatingSpinner,
  CircleCheck,
  CircleX,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { dismissDccNotification } from 'Pods/ThreeDStudio/Store/actions/dccBridge'
import { DCC_SEND_STATUS } from 'Pods/ThreeDStudio/Store/slices/dccBridge'

const AUTO_HIDE_MS = 15000

const buildIcon = (svg, color) => (
  <Icon
    svgComponent={svg}
    color={color}
    notSpaced
    size={iconSizeMedium}
  />
)

const VARIANT_BY_STATUS = {
  [DCC_SEND_STATUS.CONNECTING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    title: () => 'Connecting plugin...',
    description: null,
    dismissible: false,
  },
  [DCC_SEND_STATUS.SENDING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    title: (name) => `Sending 3D Model to ${name}...`,
    description: null,
    dismissible: false,
  },
  [DCC_SEND_STATUS.SUCCESS]: {
    type: 'positive',
    icon: buildIcon(CircleCheck, 'positive'),
    title: (name) => `3D Model is sent to ${name}`,
    description: null,
    dismissible: true,
  },
  [DCC_SEND_STATUS.PLUGIN_NOT_RUNNING]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    title: () => 'Plugin is not running',
    description: (name) => (
      `Make sure your DCC is open and the plugin is installed and running${name ? ` for ${name}` : ''}.`
    ),
    dismissible: true,
  },
  [DCC_SEND_STATUS.DCC_BUSY]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    title: () => 'DCC is busy',
    description: () => "It's still processing a previous import. Please try again in a moment.",
    dismissible: true,
  },
  [DCC_SEND_STATUS.ERROR]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    title: () => 'Something went wrong',
    description: () => 'There was a problem sending your model. Please try again.',
    dismissible: true,
  },
}

function DccNotification() {
  const sendStatus = useThreeDStudioStore(state => state.dccBridge.sendStatus)
  const sendingTo = useThreeDStudioStore(state => state.dccBridge.sendingTo)

  const variant = useMemo(() => VARIANT_BY_STATUS[sendStatus], [sendStatus])

  useEffect(() => {
    if (!variant) return undefined
    const timer = setTimeout(dismissDccNotification, AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [variant, sendingTo])

  if (!variant) return null

  const title = variant.title(sendingTo)
  const description = variant.description ? variant.description(sendingTo) : null
  return (
    <div
      className={classnames('dcc-notification', description && 'dcc-notification--with-description' )}
      role="status"
      aria-live="polite"
    >
      <Notice
        type={variant.type}
        useSSRIcon
        ssrIcon={variant.icon}
        onClose={variant.dismissible ? dismissDccNotification : null}
      >
        <div className="dcc-notification__body">
          <span className="cgt-heading-50">{title}</span>
          {description && <span className="cgt-body-base">{description}</span>}
        </div>
      </Notice>
    </div>
  )
}

export default memo(DccNotification)
